import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { api } from "../../api/api";
import { parseMaybeJson } from "../../helpers/parseMaybeJson";
import {
  IConversationState,
  IActiveSequenceInfo,
  IPendingJob,
} from "./SequenceDebug.types";

/* ===============================
   FETCH ACTIVE SEQUENCE
================================= */

export const FETCH_SEQUENCE_DEBUG_PROGRESS = "FETCH_SEQUENCE_DEBUG_PROGRESS";
export const FETCH_SEQUENCE_DEBUG_SUCCESS = "FETCH_SEQUENCE_DEBUG_SUCCESS";
export const FETCH_SEQUENCE_DEBUG_FAILED = "FETCH_SEQUENCE_DEBUG_FAILED";

export const fetchSequenceDebugProgress = () =>
  action(FETCH_SEQUENCE_DEBUG_PROGRESS);

export const fetchSequenceDebugSuccess = (
  state: IConversationState | null,
  activeSequence: IActiveSequenceInfo | null,
) => action(FETCH_SEQUENCE_DEBUG_SUCCESS, { state, activeSequence });

export const fetchSequenceDebugFailed = () =>
  action(FETCH_SEQUENCE_DEBUG_FAILED);

export const fetchActiveSequenceAsync =
  (
    mobileNo: string,
    companyOutletUuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchSequenceDebugProgress());
    dispatch(saveLoaderProgress());
    try {
      const response = await api.get(
        `/sequence/get-conversation-active-sequence?mobile_no=${encodeURIComponent(mobileNo)}&company_outlet_uuid=${companyOutletUuid}`,
      );

      const data = response.data?.data;

      if (data) {
        const conversationState: IConversationState = {
          mobile_no: data.mobile_no,
          active_sequence_uuid: data.active_sequence_uuid,
          current_step: data.current_step,
          status: data.status,
          sequence_started_at: data.sequence_started_at,
          last_user_message_at: data.last_user_message_at,
          whatsapp_window_expires_at: data.whatsapp_window_expires_at,
          last_signals: parseMaybeJson(data.last_signals, {}),
        };

        const activeSequence: IActiveSequenceInfo | null =
          data.active_sequence_uuid
            ? {
                message_sequence_uuid: data.message_sequence_uuid || data.active_sequence_uuid,
                sequence_name: data.sequence_name || "",
                category: data.category || "",
                priority: data.priority || 0,
                conditions: parseMaybeJson(data.conditions, {
                  intent: [],
                  confidence_score: 0,
                }),
              }
            : null;

        dispatch(fetchSequenceDebugSuccess(conversationState, activeSequence));
      } else {
        dispatch(fetchSequenceDebugSuccess(null, null));
      }
    } catch (err: any) {
      dispatch(fetchSequenceDebugFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message ||
            "Failed to fetch active sequence state",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

/* ===============================
   FETCH PENDING JOBS
================================= */

export const FETCH_PENDING_JOBS_PROGRESS = "FETCH_PENDING_JOBS_PROGRESS";
export const FETCH_PENDING_JOBS_SUCCESS = "FETCH_PENDING_JOBS_SUCCESS";
export const FETCH_PENDING_JOBS_FAILED = "FETCH_PENDING_JOBS_FAILED";

export const fetchPendingJobsProgress = () =>
  action(FETCH_PENDING_JOBS_PROGRESS);

export const fetchPendingJobsSuccess = (data: IPendingJob[]) =>
  action(FETCH_PENDING_JOBS_SUCCESS, { data });

export const fetchPendingJobsFailed = () => action(FETCH_PENDING_JOBS_FAILED);

export const fetchPendingJobsAsync =
  (mobileNo: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchPendingJobsProgress());
    try {
      const response = await api.get(
        `/sequence/get-pending-sequence-jobs?mobile_no=${encodeURIComponent(mobileNo)}`,
      );

      const result = response.data?.data || [];
      dispatch(fetchPendingJobsSuccess(result));
    } catch (err: any) {
      dispatch(fetchPendingJobsFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch pending jobs",
          displayAs: "snackbar",
        }),
      );
    }
  };

/* ===============================
   FORCE STOP SEQUENCE
================================= */

export const forceStopSequenceAsync =
  (
    mobileNo: string,
    companyOutletUuid: string,
    onCallback?: (success: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const response = await api.put(
        `/sequence/force-stop-sequence?mobile_no=${encodeURIComponent(mobileNo)}&company_outlet_uuid=${companyOutletUuid}`,
        {},
      );

      dispatch(
        showMessage({
          type: "success",
          message: response.data?.message || "Sequence force stopped successfully",
          displayAs: "snackbar",
        }),
      );

      onCallback?.(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to force stop sequence",
          displayAs: "snackbar",
        }),
      );
      onCallback?.(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

/* ===============================
   FORCE SWITCH SEQUENCE
================================= */

export const forceSwitchSequenceAsync =
  (
    mobileNo: string,
    companyOutletUuid: string,
    messageSequenceUuid: string,
    onCallback?: (success: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const response = await api.put(
        `/sequence/force-switch-sequence?mobile_no=${encodeURIComponent(mobileNo)}&company_outlet_uuid=${companyOutletUuid}&message_sequence_uuid=${messageSequenceUuid}`,
      );

      dispatch(
        showMessage({
          type: "success",
          message:
            response.data?.message || "Sequence switched successfully",
          displayAs: "snackbar",
        }),
      );

      onCallback?.(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to switch sequence",
          displayAs: "snackbar",
        }),
      );
      onCallback?.(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

/* ===============================
   CLEAR STATE
================================= */

export const CLEAR_SEQUENCE_DEBUG_STATE = "CLEAR_SEQUENCE_DEBUG_STATE";

export const clearSequenceDebugState = () =>
  action(CLEAR_SEQUENCE_DEBUG_STATE);
